import React from 'react';
import { Table } from 'react-bootstrap';
import { SportProvider } from '../../models/interfaces/SportProvider';
import { MatchSegmentStats, StatType } from '../../models/interfaces/Stat';
import { RosterEntry } from '../../models/interfaces/RosterEntry';
import { Match } from '../../models/interfaces/Match';
import { MatchEntry } from '../../models/interfaces/MatchEntry';
import PlayerView from '../players/PlayerView';
import TeamView from '../../components/containers/TeamView';
import { Link } from 'react-router-dom';

interface Props {
    match: Match;
    teamIDFilter?: string;
    sportProvider: SportProvider;
    matchStatsList: MatchSegmentStats[];
}

interface TeamData {
    team: MatchEntry;
    columnNames: string[];
    rows: RowData[];
}

interface RowData {
    player: RosterEntry;
    statCounts: number[];
}

interface TeamSectionProps {
    teamData: TeamData;
    showsTeamRow: boolean;
}

function TeamSection({ teamData, showsTeamRow }: TeamSectionProps) {
    return (
        <>
            <thead>
                {showsTeamRow && (
                    <tr>
                        <th>
                            <TeamView name={teamData.team.name} />
                        </th>
                    </tr>
                )}
                <tr>
                    {teamData.columnNames.map((columnName) => {
                        return <th key={columnName}>{columnName}</th>;
                    })}
                </tr>
            </thead>
            <tbody>
                {teamData.rows.map((row) => {
                    return (
                        <tr key={row.player.id}>
                            <td>
                                {
                                    row.player.backingPlayer ? 
                                    <Link to={`/players/${row.player.backingPlayer.id}`}>
                                        <PlayerView player={row.player} />
                                    </Link>
                                    : 
                                        <PlayerView player={row.player} />
                                }
                                
                            </td>
                            {row.statCounts.map((count, i) => {
                                return (
                                    <td key={`${row.player.id}-${teamData.columnNames[i]}`}>
                                        {count}
                                    </td>
                                );
                            })}
                        </tr>
                    );
                })}
            </tbody>
        </>
    );
}

export default function MatchTeamsTable({
    match,
    teamIDFilter,
    sportProvider,
    matchStatsList,
}: Props) {
    const statDescriptionsToDisplay = sportProvider.matchStatDescriptionsToDisplay();
    const teams = [match.lightCapTeam, match.darkCapTeam].filter((team) => {
        if (teamIDFilter) {
            return team.id === teamIDFilter;
        } else {
            return true;
        }
    });
    let playerMap = new Map<string, RosterEntry>();
    const statsForTeams: Map<string, Map<string, Map<StatType, number>>> = matchStatsList.reduce(
        (currentTeamStats, matchStats) => {
            matchStats.stats.forEach((stat) => {
                if (!stat.team) {
                    return
                }
                const teamID = stat.team.id;
                if (teamIDFilter && teamIDFilter !== teamID) {
                    return;
                }
                const player = stat.player;
                if (!player) {
                    return;
                }
                const playerID = player.id;
                playerMap.set(playerID, player);

                var teamStats = currentTeamStats.get(teamID);
                if (!teamStats) {
                    teamStats = new Map<string, Map<StatType, number>>();
                }
                var playerStats = teamStats.get(playerID);
                if (!playerStats) {
                    playerStats = new Map<StatType, number>();
                }
                var countForStats = playerStats.get(stat.description.dataType);
                if (!countForStats) {
                    countForStats = 0;
                }
                countForStats += 1;
                playerStats.set(stat.description.dataType, countForStats);
                teamStats.set(playerID, playerStats);
                currentTeamStats.set(teamID, teamStats);
            });
            return currentTeamStats;
        },
        new Map<string, Map<string, Map<StatType, number>>>()
    );

    const teamData: TeamData[] = teams.map((team) => {
        var rows = new Array<RowData>();
        const playerIDStats = statsForTeams.get(team.id);
        if (playerIDStats) {
            const sortedPlayerIDs = Array.from(playerIDStats.keys()).sort((left, right) => {
                const leftPlayer = playerMap.get(left);
                const rightPlayer = playerMap.get(right);
                if (!leftPlayer || !rightPlayer) {
                    return 0;
                }
                return leftPlayer.capNumber.sortValue - rightPlayer.capNumber.sortValue;
            });
            rows = sortedPlayerIDs.reduce((rowDatas, playerID) => {
                const player = playerMap.get(playerID);
                if (!player) {
                    return rowDatas;
                }
                const playerCounts = statDescriptionsToDisplay.map((statDescription) => {
                    const playerStats = playerIDStats.get(playerID);
                    if (!playerStats) {
                        return 0;
                    } else {
                        const count = playerStats.get(statDescription.dataType);
                        if (!count) {
                            return 0;
                        } else {
                            return count;
                        }
                    }
                });
                const rowData: RowData = {
                    player: player,
                    statCounts: playerCounts,
                };
                rowDatas.push(rowData);
                return rowDatas;
            }, new Array<RowData>());
        }
        return {
            team: team,
            columnNames: ['Player'].concat(
                statDescriptionsToDisplay.map((statDescription) => statDescription.dataType.displayName)
            ),
            rows: rows,
        };
    });
    return (
        <Table striped bordered responsive>
            {teamData.map((teamSection) => (
                <TeamSection
                    key={`table-${teamSection.columnNames[0]}`}
                    teamData={teamSection}
                    showsTeamRow={!!!teamIDFilter}
                />
            ))}
        </Table>
    );
}
